/**
 * 🧰 [Phase 17.5] Section "Normes par unité" pour la fiche article.
 *
 * Permet à l'owner de créer/modifier/supprimer les normes liées à un article.
 * Affiché uniquement quand on édite un article existant (articleId défini).
 * Pour un nouveau produit pas encore sauvegardé, on cache le bloc — le user
 * sauvegarde d'abord, puis rouvre l'article pour définir les normes.
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Trash2, Edit2, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function NormesArticleSection({ articleId, canEdit }) {
  const [normes, setNormes] = useState([]);
  const [unites, setUnites] = useState([]);
  const [editing, setEditing] = useState(null); // null | norme | "new"
  const [form, setForm] = useState({ nom: '', quantite: '', unite_ids: [] });
  const [filtreCategorie, setFiltreCategorie] = useState('all');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!articleId) return;
    setLoading(true);
    try {
      const [normesRes, unitesRes] = await Promise.all([
        axios.get(`${API_URL}/api/normes-materiel?article_id=${articleId}`),
        axios.get(`${API_URL}/api/vehicules`).catch(() => ({ data: { vehicules: [] } })),
      ]);
      setNormes(normesRes.data.normes || []);
      const list = unitesRes.data?.vehicules || unitesRes.data || [];
      setUnites(Array.isArray(list) ? list : []);
    } catch (e) {
      toast.error("Erreur de chargement des normes");
    } finally {
      setLoading(false);
    }
  }, [articleId]);

  useEffect(() => { load(); }, [load]);

  // Map des unités déjà prises (autres normes) → norme.nom
  const uniteDejaDans = {};
  normes.forEach(n => {
    if (editing && editing !== 'new' && n.id === editing.id) return;
    (n.unite_ids || []).forEach(uid => {
      uniteDejaDans[uid] = n.nom || '(sans nom)';
    });
  });

  const openNew = () => {
    setEditing('new');
    setForm({ nom: '', quantite: '', unite_ids: [] });
  };

  const openEdit = (n) => {
    setEditing(n);
    setForm({
      nom: n.nom || '',
      quantite: String(n.quantite || ''),
      unite_ids: [...(n.unite_ids || [])],
    });
  };

  const cancel = () => {
    setEditing(null);
    setForm({ nom: '', quantite: '', unite_ids: [] });
  };

  const toggleUnite = (uid) => {
    setForm(f => {
      const set = new Set(f.unite_ids);
      if (set.has(uid)) set.delete(uid); else set.add(uid);
      return { ...f, unite_ids: Array.from(set) };
    });
  };

  const save = async () => {
    const qte = parseFloat(form.quantite);
    if (!qte || qte <= 0) { toast.error("Quantité requise (> 0)"); return; }
    if (form.unite_ids.length === 0) { toast.error("Sélectionnez au moins une unité"); return; }
    const payload = {
      article_id: articleId,
      quantite: qte,
      unite_ids: form.unite_ids,
      nom: form.nom?.trim() || null,
    };
    try {
      if (editing === 'new') {
        await axios.post(`${API_URL}/api/normes-materiel`, payload);
        toast.success("Norme créée");
      } else {
        await axios.put(`${API_URL}/api/normes-materiel/${editing.id}`, {
          quantite: qte, unite_ids: form.unite_ids, nom: payload.nom,
        });
        toast.success("Norme mise à jour");
      }
      cancel();
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === 'object' && detail?.code === 'unite_deja_dans_norme') {
        const conflits = detail.conflits || [];
        const noms = conflits.map(c => c.nom).join(', ');
        toast.error(`Une ou plusieurs unités sont déjà dans la norme : ${noms}`);
      } else {
        toast.error(typeof detail === 'string' ? detail : "Erreur d'enregistrement");
      }
    }
  };

  const remove = async (n) => {
    if (!window.confirm(`Supprimer la norme "${n.nom || '(sans nom)'}" ?`)) return;
    try {
      await axios.delete(`${API_URL}/api/normes-materiel/${n.id}`);
      toast.success("Norme supprimée");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur suppression");
    }
  };

  if (!canEdit) return null;
  if (!articleId) {
    return (
      <div className="border-t pt-3 mt-3 text-xs text-slate-400 italic" data-testid="normes-section-pending">
        💡 Sauvegardez le produit d'abord pour pouvoir définir des normes par unité.
      </div>
    );
  }

  const unitesAffichees = unites.filter(u =>
    filtreCategorie === 'all' || (u.categorie || '').toLowerCase() === filtreCategorie
  );

  return (
    <div className="border-t pt-4 mt-4 space-y-2" data-testid="normes-section">
      <div>
        <h4 className="text-sm font-semibold text-slate-800">🧰 Normes par unité (optionnel)</h4>
        <p className="text-xs text-slate-500 mb-2">Définissez la quantité requise de cet article pour des groupes d'unités. Utilisé par la page Comparaison matériel.</p>
      </div>

      {loading && <p className="text-xs text-slate-400">Chargement...</p>}

      {normes.length === 0 && !editing && (
        <p className="text-xs text-slate-400 italic" data-testid="normes-empty">Aucune norme définie pour cet article.</p>
      )}

      {normes.map(n => (
        <div key={n.id} className="border rounded p-2 bg-slate-50 flex items-start gap-2" data-testid={`norme-row-${n.id}`}>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium">
              {n.nom || <span className="italic text-slate-500">(sans nom)</span>}
              <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 rounded">{n.quantite} unité{n.quantite > 1 ? 's' : ''}</span>
            </div>
            <div className="flex flex-wrap gap-1 mt-1">
              {(n.unite_ids || []).map(uid => {
                const u = unites.find(x => x.id === uid);
                return (
                  <span key={uid} className="text-[10px] bg-white border px-1.5 py-0.5 rounded">
                    {u ? (u.numero_unite ? `#${u.numero_unite}` : u.nom) : uid.slice(0, 6)}
                  </span>
                );
              })}
            </div>
          </div>
          <button onClick={() => openEdit(n)} className="text-slate-500 hover:text-blue-600 p-1" data-testid={`btn-edit-norme-${n.id}`}>
            <Edit2 className="w-4 h-4" />
          </button>
          <button onClick={() => remove(n)} className="text-slate-500 hover:text-red-600 p-1" data-testid={`btn-delete-norme-${n.id}`}>
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}

      {!editing && (
        <Button variant="outline" size="sm" onClick={openNew} data-testid="btn-add-norme">
          <Plus className="w-4 h-4 mr-1" /> Ajouter une norme
        </Button>
      )}

      {editing && (
        <div className="border-2 border-blue-300 rounded p-3 bg-blue-50/40 space-y-2" data-testid="norme-form">
          <div className="flex items-center justify-between">
            <h5 className="text-sm font-semibold">{editing === 'new' ? 'Nouvelle norme' : `Modifier "${editing.nom || '(sans nom)'}"`}</h5>
            <button onClick={cancel} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Nom (optionnel)</Label>
              <Input value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))} placeholder="ex: Vans travail" className="h-8" data-testid="input-norme-nom" />
            </div>
            <div>
              <Label className="text-xs">Quantité requise *</Label>
              <Input type="number" min="1" step="0.1" value={form.quantite} onChange={e => setForm(f => ({ ...f, quantite: e.target.value }))} className="h-8" data-testid="input-norme-quantite" />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs">Unités concernées ({form.unite_ids.length}/{unitesAffichees.length})</Label>
              <select
                className="text-xs border rounded px-1 py-0.5"
                value={filtreCategorie}
                onChange={e => setFiltreCategorie(e.target.value)}
                data-testid="select-categorie-filter"
              >
                <option value="all">Toutes</option>
                <option value="travail">Travail</option>
                <option value="rangement">Rangement</option>
                <option value="elevation">Élévation</option>
              </select>
            </div>
            <div className="border rounded p-2 max-h-48 overflow-y-auto bg-white space-y-1" data-testid="unites-list">
              {unitesAffichees.length === 0 && <p className="text-xs text-slate-400 italic">Aucune unité</p>}
              {unitesAffichees.map(u => {
                const dejaDans = uniteDejaDans[u.id];
                const selected = form.unite_ids.includes(u.id);
                return (
                  <label key={u.id} className={`flex items-center gap-2 text-xs cursor-pointer ${dejaDans ? 'opacity-60' : ''}`} title={dejaDans ? `Déjà dans la norme "${dejaDans}"` : ''}>
                    <Checkbox
                      checked={selected}
                      disabled={!!dejaDans && !selected}
                      onCheckedChange={() => !dejaDans || selected ? toggleUnite(u.id) : null}
                      data-testid={`norme-check-${u.id}`}
                    />
                    <span>{u.numero_unite ? `#${u.numero_unite}` : ''} {u.nom}</span>
                    {dejaDans && !selected && (
                      <span className="text-amber-600 text-[10px]">⚠ Déjà dans "{dejaDans}"</span>
                    )}
                  </label>
                );
              })}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={cancel} data-testid="btn-norme-cancel">Annuler</Button>
            <Button size="sm" onClick={save} data-testid="btn-norme-save">Enregistrer</Button>
          </div>
        </div>
      )}
    </div>
  );
}
